/*
 * Copyright '2024' Dell Inc. or its subsidiaries. All Rights Reserved.
 */

import {IEventLogger} from "./event-logger.interface";
import {IEvent} from "sirius-platform-support-library/shared/event-bus/event.interface";

export class EventDatabaseLogger implements IEventLogger {
    public static readonly DATABASE_NAME = 'sirius-shell';
    public static readonly ONE_WEEK_INTERVAL = (7 * 24 * 3600 * 1000);

    private initialized = false;
    private context: any;
    private db: IDBDatabase;

    constructor(context: any) {
        this.context = context;
    }

    public async log<T>(event: IEvent<T>): Promise<void> {
        await this.initialize();

        const transaction = this.db.transaction(['events'], 'readwrite');
        if (!transaction) {
            return;
        }
        const objectStore = transaction.objectStore('events');
        if (!objectStore) {
            return;
        }
        await objectStore.add(event);
        transaction.commit();
    }

    private async initialize() {
        try {
            if (this.initialized) {
                return;
            }
            this.db = await this.getDatabase();
        } catch (e) {
            throw e;
        } finally {
            this.initialized = true;
        }
    }

    private async getDatabase(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const dbRequest = this.context.indexedDB.open(EventDatabaseLogger.DATABASE_NAME, 1);
            dbRequest.onupgradeneeded = (event) => {
                this.buildSchema(event.target.result);
            }
            dbRequest.onerror = (event) => {
                reject(event)
            };
            dbRequest.onsuccess = (event) => {
                const database = event.target.result;
                this.purge(database);
                resolve(database);
            }
        });
    }

    private buildSchema(database: IDBDatabase) {
        const eventsObjectStore = database.createObjectStore("events", {
            autoIncrement: false,
            keyPath: 'id'
        });
        eventsObjectStore.createIndex("id", "id", {unique: true});
        eventsObjectStore.createIndex("timestamp", "timestamp", {unique: false});
        eventsObjectStore.createIndex("dispatcherName", "dispatcherName", {unique: false});
        eventsObjectStore.createIndex("subscriberName", "subscriberName", {unique: false});
        eventsObjectStore.createIndex("type", "type", {unique: false});
    }

    private purge(database: IDBDatabase) {
        const transaction = database.transaction(["events"], 'readwrite');
        const objectStore = transaction.objectStore("events");
        const weekAgo = new Date(Date.now() - EventDatabaseLogger.ONE_WEEK_INTERVAL);
        const range = IDBKeyRange.upperBound(weekAgo);
        const index = objectStore.index("timestamp");

        index.openCursor(range).onsuccess = event => {
            const cursor = event.target['result'];

            if (cursor) {
                objectStore.delete(cursor.key);
                cursor.continue();
            }
        };
    }
}
